<script lang="ts">
  import { requestPasswordReset } from '../../lib/api';
  import LoadingSpinner from '../ui/LoadingSpinner.svelte';

  let email = $state('');
  let error = $state('');
  let success = $state(false);
  let loading = $state(false);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    error = '';
    success = false;
    loading = true;

    const result = await requestPasswordReset(email);

    if (result.success) {
      success = true;
    } else {
      error = result.error || 'Failed to send reset email';
    }

    loading = false;
  }

  function navigateTo(path: string) {
    window.location.hash = path;
  }
</script>

<div class="forgot-container">
  <!-- Left Panel - Form -->
  <div class="form-panel">
    <div class="form-content">
      <!-- Logo and Header -->
      <div class="header-section">
        <div class="logo-container">
          <img src="/console/assets/app_logo.png" alt="EQUOS" class="logo-icon" />
          <span class="logo-text">EQUOS9</span>
        </div>
        <h1>Forgot Password?</h1>
        <p class="subtitle">Enter your email address and we'll send you a link to reset your password</p>
      </div>

      <!-- Success Message -->
      {#if success}
        <div class="success-message" role="status">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <div class="success-content">
            <strong>Check your email!</strong>
            <span>If an account exists with this email, you'll receive a password reset link.</span>
          </div>
        </div>
      {/if}

      <!-- Error Message -->
      {#if error}
        <div class="error-message" role="alert">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{error}</span>
        </div>
      {/if}

      <!-- Form -->
      {#if !success}
        <form onsubmit={handleSubmit}>
          <div class="form-group">
            <div class="input-wrapper">
              <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <input
                id="email"
                type="email"
                bind:value={email}
                required
                placeholder="Email address"
                disabled={loading}
                autocomplete="email"
              />
            </div>
          </div>

          <button type="submit" class="btn-primary" disabled={loading}>
            {#if loading}
              <LoadingSpinner size="small" light />
              <span>Sending...</span>
            {:else}
              <span>SEND RESET LINK</span>
            {/if}
          </button>
        </form>
      {:else}
        <button type="button" class="btn-primary" onclick={() => navigateTo('login')}>
          <span>BACK TO LOGIN</span>
        </button>
      {/if}

      <!-- Back to Login -->
      {#if !success}
        <div class="links">
          <button
            type="button"
            class="back-link"
            onclick={() => navigateTo('login')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            <span>Back to sign in</span>
          </button>
        </div>
      {/if}

      <!-- Divider -->
      <div class="divider">
        <span>Or</span>
      </div>

      <!-- Create Account -->
      <p class="signup-link">
        Don't have an account?
        <button type="button" onclick={() => navigateTo('signup')}>Sign Up Now</button>
      </p>
    </div>
  </div>

  <!-- Right Panel - Branding -->
  <div class="brand-panel">
    <div class="brand-content">
      <!-- Decorative circles -->
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>

      <!-- Illustration -->
      <div class="illustration">
        <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="60" cy="60" r="50" fill="rgba(255,255,255,0.1)"/>
          <rect x="35" y="45" width="50" height="35" rx="3" stroke="white" stroke-width="2" fill="none"/>
          <path d="M35 50l25 18 25-18" stroke="white" stroke-width="2" fill="none"/>
          <circle cx="85" cy="45" r="12" fill="#4A7DFF" stroke="white" stroke-width="2"/>
          <path d="M82 45l3 3 5-5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <h2 class="brand-title">Password Recovery</h2>
      <p class="brand-description">
        We'll send you secure instructions to reset your password and get you back into your account.
      </p>
    </div>
  </div>
</div>

<style>
  .forgot-container {
    min-height: 100vh;
    display: flex;
    flex-direction: row;
  }

  /* ===== LEFT PANEL - FORM ===== */
  .form-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    background: #f8f9fc;
  }

  .form-content {
    width: 100%;
    max-width: 380px;
  }

  /* Header Section */
  .header-section {
    text-align: center;
    margin-bottom: 2rem;
  }

  .logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .logo-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }

  .logo-text {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: -0.02em;
  }

  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  /* Success Message */
  .success-message {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #047857;
    padding: 1rem;
    border-radius: 10px;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
    animation: slideIn 0.3s ease-out;
  }

  .success-message svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .success-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .success-content strong {
    font-weight: 600;
  }

  .success-content span {
    opacity: 0.9;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Error Message */
  .error-message {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 0.875rem 1rem;
    border-radius: 10px;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
    animation: shake 0.5s ease-in-out;
  }

  .error-message svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-5px); }
    40%, 80% { transform: translateX(5px); }
  }

  /* Form */
  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .input-icon {
    position: absolute;
    left: 1rem;
    width: 20px;
    height: 20px;
    color: #9ca3af;
    pointer-events: none;
    transition: color 0.2s ease;
  }

  .input-wrapper:focus-within .input-icon {
    color: #4A7DFF;
  }

  input[type="email"] {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 3rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 1rem;
    background: white;
    color: #1f2937;
    transition: all 0.2s ease;
  }

  input::placeholder {
    color: #9ca3af;
  }

  input:focus {
    outline: none;
    border-color: #4A7DFF;
    box-shadow: 0 0 0 3px rgba(74, 125, 255, 0.1);
  }

  input:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }

  /* Submit Button */
  .btn-primary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.875rem 1.5rem;
    margin-top: 0.5rem;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 0.9375rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(74, 125, 255, 0.35);
  }

  .btn-primary:active:not(:disabled) {
    transform: translateY(0);
  }

  .btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  /* Links */
  .links {
    margin-top: 1.5rem;
    display: flex;
    justify-content: center;
  }

  .back-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    color: #4A7DFF;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0.5rem 1rem;
    border-radius: 8px;
  }

  .back-link:hover {
    background: rgba(74, 125, 255, 0.1);
  }

  .back-link svg {
    width: 18px;
    height: 18px;
  }

  /* Divider */
  .divider {
    display: flex;
    align-items: center;
    margin: 1.5rem 0;
    gap: 1rem;
  }

  .divider::before,
  .divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }

  .divider span {
    font-size: 0.75rem;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  /* Sign Up Link */
  .signup-link {
    text-align: center;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .signup-link button {
    background: none;
    border: none;
    color: #4A7DFF;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
    transition: color 0.2s ease;
  }

  .signup-link button:hover {
    color: #3b6de0;
  }

  /* ===== RIGHT PANEL - BRANDING ===== */
  .brand-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    position: relative;
    overflow: hidden;
  }

  .brand-content {
    text-align: center;
    color: white;
    z-index: 1;
    padding: 2rem;
  }

  /* Decorative Circles */
  .circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
  }

  .circle-1 {
    width: 300px;
    height: 300px;
    top: -100px;
    right: -50px;
  }

  .circle-2 {
    width: 200px;
    height: 200px;
    bottom: -50px;
    left: -50px;
  }

  .circle-3 {
    width: 150px;
    height: 150px;
    top: 50%;
    right: 10%;
    background: rgba(255, 255, 255, 0.05);
  }

  .illustration {
    width: 140px;
    height: 140px;
    margin: 0 auto 1.5rem;
  }

  .illustration svg {
    width: 100%;
    height: 100%;
  }

  .brand-title {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    letter-spacing: -0.02em;
  }

  .brand-description {
    font-size: 1rem;
    line-height: 1.6;
    opacity: 0.9;
    max-width: 300px;
    margin: 0 auto;
  }

  /* ===== RESPONSIVE ===== */
  @media (max-width: 968px) {
    .forgot-container {
      flex-direction: column-reverse;
    }

    .brand-panel {
      padding: 3rem 2rem;
      min-height: auto;
    }

    .illustration {
      width: 100px;
      height: 100px;
      margin-bottom: 1rem;
    }

    .brand-title {
      font-size: 1.5rem;
    }

    .brand-description {
      font-size: 0.875rem;
    }

    .circle-1 {
      width: 150px;
      height: 150px;
      top: -50px;
      right: -30px;
    }

    .circle-2 {
      width: 100px;
      height: 100px;
      bottom: -30px;
      left: -30px;
    }

    .circle-3 {
      display: none;
    }

    .form-panel {
      padding: 2rem 1.5rem 3rem;
    }
  }

  @media (max-width: 480px) {
    .form-content {
      max-width: 100%;
    }

    .logo-text {
      font-size: 1.25rem;
    }

    h1 {
      font-size: 1.5rem;
    }
  }
</style>
